import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { order as orderType, product as productType } from '@cashew/types';
import { Box } from '../../components';
import ListInstallments from './ListInstallments';
import { UserState } from '../../reducers/user';
import { RootState } from '../../reducers';
import { initialOrder } from '../../helpers/initialorder';

import './index.css';

const { api, priceFormat } = require('@cashew/utils');

const ns = 'order-details';

const getOrderDetails = (orderId: any, setOrderDetails: any, dispatch: any) => {
  api
    .get({
      endpoint: `orders/${orderId}`,
    })
    .then((resp: any) => {
      setOrderDetails(resp.data);
    })
    .catch(() => {
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: 'Fetch order',
          toasterMessage: 'There was an issue with order details. Try again.',
          toasterType: 'error',
          toasterVisible: true,
        },
      });
    });
};

interface ContentProps {
  orderId: string;
  product: productType.ProductType | any;
  paidAmount: number;
  nextPayment: {
    installmentAmount: number;
    latePaymentAmount: number;
    latePaymentAmountVat: number;
    chargeDate: string;
  };
}
const Content: React.FC<ContentProps> = ({
  orderId,
  nextPayment,
  product,
  paidAmount,
}) => {
  const { t } = useTranslation();
  const { token }: UserState = useSelector((state: RootState) => state.user);
  api.storeAuth(token);
  const dispatch = useDispatch();
  const query = new URLSearchParams(useLocation().search);
  const params: any = useParams();
  const selectedOrder = orderId || params.orderId;
  const [orderDetails, setOrderDetails] = useState<orderType.OrderType>(
    initialOrder as unknown as orderType.OrderType
  );

  const [balance, setBalance] = useState(-1);

  const isConfirmation = query.get('confirmation');

  useEffect(() => {
    if (!selectedOrder) {
      return;
    }
    getOrderDetails(selectedOrder, setOrderDetails, dispatch);
    api
      .get({
        endpoint: `orders/${selectedOrder}/balance`,
      })
      .then((resp: any) => {
        const { data } = resp;
        setBalance(data.orderBalance);
      })
      .catch((error: any) => {
        dispatch({
          type: 'OPEN_TOASTER',
          payload: {
            toasterTitle: error?.response?.data?.errorCode,
            toasterMessage: error?.response?.data?.message,
            toasterType: 'error',
            toasterVisible: true,
            toasterDismiss: null,
          },
        });
      });
  }, [token, selectedOrder]);
  useEffect(() => {
    const code = query.get('code') || '';
    dispatch({
      type: 'OPEN_TOASTER',
      payload: {
        toasterTitle: isConfirmation ? 'Payment' : 'Error on payment',
        toasterMessage: isConfirmation
          ? 'We received your payment'
          : t(`error.${code}`) || '',
        toasterType: isConfirmation ? 'success' : 'error',
        toasterVisible: !!(query.get('code') || query.get('confirmation')),
        toasterDismiss: null,
      },
    });
  }, [query.get('code'), query.get('confirmation')]);

  const statusLabel =
    orderDetails?.orderStatus === 'CREATED' ? 'Not Paid' : 'Paid';

  const orderTypeText = useMemo(() => {
    let type = t(`product.${product?.type}`, {
      number: product?.subProducts[0].installments.length,
    });

    if (product?.subProducts[0].type === 'DIVIDE1') {
      type = 'Pay now';
    }
    return type;
  }, [product, orderDetails]);

  return selectedOrder ? (
    <div className={ns}>
      <Box withHeader className={`${ns}__box--content`}>
        <div className={`${ns}__cols`}>
          <div>
            <div className={`${ns}__box-grey`}>
              <h2>Order items</h2>
            </div>
            <div className={`${ns}__box-grey ${ns}__items`}>
              <ul>
                {orderDetails.items?.map((item) => (
                  <li key={item.id}>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', color: '#333333' }}
                    >
                      <img
                        width={81}
                        height={81}
                        src={
                          item.image
                            ? item.image
                            : 'https://cdn.cashewpayments.com/images/item-placeholder.png'
                        }
                        alt=""
                      />
                      <p>{item.reference}</p>
                      <p>
                        Unit price:
                        {item.unitPrice}
                      </p>
                      <p>
                        Quantity:
                        {item.quantity}
                      </p>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`${ns}__box-grey ${ns}__transactions`}>
              <h2>Transaction Details</h2>
            </div>
            <div className={`${ns}__box-grey`}>
              <p>
                Paid
                <span className={`${ns}__label`}>
                  {priceFormat({
                    currency: orderDetails.currencyCode,
                    amount: paidAmount,
                  })}
                </span>
              </p>
              <p>
                Remaining
                <span className={`${ns}__label`}>
                  {priceFormat({
                    currency: orderDetails.currencyCode,
                    amount: balance,
                  })}
                </span>
              </p>
            </div>
          </div>
          <div>
            <div className={`${ns}__box-grey`}>
              <h2>{t('orderDetails')}</h2>
            </div>
            <div className={`${ns}__box-grey`}>
              <p>
                Store:
                <span className={`${ns}__label`}>
                  {orderDetails.store?.storeName}
                </span>
              </p>
              <p>
                {t('orderDetails.paymentMethod')}
                <span className={`${ns}__label`}>{orderTypeText}</span>
              </p>
            </div>
            <div className={`${ns}__box-grey ${ns}__due-amount`}>
              {nextPayment && <>Payment due: </>}
              <strong>
                {nextPayment
                  ? priceFormat({
                      currency: orderDetails.currencyCode,
                      amount:
                        nextPayment?.installmentAmount +
                        nextPayment?.latePaymentAmount +
                        nextPayment?.latePaymentAmountVat,
                    })
                  : statusLabel}
              </strong>
            </div>
            {product && product?.subProducts[0]?.type !== 'DIVIDE1' && (
              <ListInstallments
                t={t}
                ns={ns}
                product={product}
                orderDetails={orderDetails}
              />
            )}
          </div>
        </div>
      </Box>
    </div>
  ) : (
    <div />
  );
};

export default Content;
